import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';

/**
 * Company Type coordinator store — thin coordinator for RHF
 *
 * Validation logic lives in react-hook-form inside CompanyTypePage.tsx.
 * This store exists only to:
 *  - Expose isValid to use-step-validation (stepper gating)
 *  - Expose formData (companyType, companyStatus) to other stores/schemas
 *  - Expose clearAllOtherStores() for cascade reset on type change
 */

export interface CompanyTypeFormData {
  companyType: string;
  companyStatus?: string;
}

interface CompanyTypeState extends BaseStoreState {
  formData: CompanyTypeFormData;
  isValid: boolean;
}

export interface CompanyTypeStore extends CompanyTypeState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<CompanyTypeFormData>) => void;
  clearAllOtherStores: () => void;
  resetForm: () => void;
  clearStore: () => void;
}

const initialFormData: CompanyTypeFormData = {
  companyType: '',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  companyStatus: '' as any,
};

export const useCompanyTypeStore = createPersistedStore<CompanyTypeState>(
  'company-type',
  {
    formData: initialFormData,
    isValid: false,
  },
  (set) => ({
    setValidity: (isValid: boolean) => set({ isValid }),

    setFormData: (data: Partial<CompanyTypeFormData>) =>
      set((state: CompanyTypeState) => ({
        formData: { ...state.formData, ...data },
      })),

    clearAllOtherStores: () => {
      // Dynamic import to avoid circular dependencies with store-manager
      import('../../../core/stores/store-manager').then(
        ({ OnboardingStoreManager }) => {
          OnboardingStoreManager.clearAllStores();
        }
      );
    },

    resetForm: () => set({ formData: initialFormData, isValid: false }),

    clearStore: () => set({ formData: initialFormData, isValid: false }),
  }),
  {
    partialize: (state) => ({
      formData: state.formData,
      isValid: state.isValid,
    }),
  }
) as unknown as PersistedStoreHook<CompanyTypeStore>;

export const useCompanyTypeFormData = () =>
  useCompanyTypeStore((state) => state.formData);
export const useCompanyTypeIsValid = () =>
  useCompanyTypeStore((state) => state.isValid);
// Stub selectors kept for backward-compatibility (no longer backed by store state)
export const useCompanyTypeErrors = () =>
  ({}) as Partial<Record<keyof CompanyTypeFormData, string>>;
export const useCompanyTypeIsDirty = () => false;
export const useCompanyTypeLoading = () => false;
